import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";

const WA_LINK = "https://wa.me/919035083242?text=Hi%20TheKhoj!%20I%27d%20like%20to%20know%20more%20about%20the%20Career%20Exploration%20Program.";

const lines = [
  { text: "Start Exploring", cls: "text-foreground" },
  { text: "Where Passion Meets Profession... Let's Explore Careers that Excite You!!", cls: "text-accent" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 grain-overlay" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-6xl lg:text-7xl" data-testid="hero-title">
              {lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${line.cls}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ delay: 0.3 + i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl"
              data-testid="hero-subtext"
            >
              With 20+ years of experience in people development space, as an authorised career exploration practitioner — TheKhoj helps Grade 9–12 students find careers they will love… identify their direction based on their intrinsic interests, explore and choose majors — ultimately helping them make informed career choices. Not a guess. Not a trend. A real foundation for a fulfilling future — leveraging a deeply researched, globally trusted assessment — 85 years of matching students with their futures.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.7 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <a
                href="#contact"
                data-testid="hero-cta-enquire"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm sm:text-base hover:opacity-90 transition-opacity"
              >
                Reach Out for a Free Orientation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-7 py-3.5 rounded-full border-2 border-primary text-primary font-semibold text-sm sm:text-base hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Say Hello on WhatsApp — We'd Love to Chat
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.div style={{ y: imgY }} className="rounded-[2rem] overflow-hidden shadow-2xl shadow-primary/20 aspect-[4/3] sm:aspect-[4/5]">
                <img
                  src="/images/hero_mentor.png"
                  alt="Indian student in a comfortable career exploration conversation with a practitioner, holding a career profile report"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
              </motion.div>
            <motion.div
              style={{ y: badgeY }}
              className="absolute -bottom-6 left-2 sm:-left-8 bg-card border border-border rounded-2xl shadow-xl px-5 py-4"
                data-testid="hero-badge"
              >
                <p className="font-display text-2xl font-extrabold text-primary">20+ yrs</p>
                <p className="text-xs text-muted-foreground font-medium">Certified People<br />Development Practitioner</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, rotate: -8 }}
                animate={{ opacity: 1, rotate: 6 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="absolute -top-4 left-3 sm:left-auto sm:-right-3 bg-accent text-accent-foreground rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider shadow-lg"
                data-testid="hero-grades-badge"
              >
                Grades 9–12
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
